/*
 * RebateAgreementCreateVersionPage - Multi-step wizard for creating new rebate agreement versions
 *
 * UI/UX DESCRIPTION:
 * - 3-step wizard with stepper navigation
 * - Step 1: Upload PDFs and AI extraction
 * - Step 2: Review extracted rules with restore capability
 * - Step 3: Preview delta analysis and create version
 * - Progress tracking and error handling throughout
 *
 * FUNCTIONALITY:
 * - Create new version from existing agreement
 * - Upload PDFs for AI rule extraction
 * - Compare new rules with previous version
 * - Restore old rules to preserve allocations/manual payments
 * - Preview impact on CSVs, allocations, and manual payments
 * - Create draft version with CSV migration
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  CheckIcon,
  UploadIcon,
  FileTextIcon,
  WarningIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Progress } from 'src/components/ui/progress';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { cn } from 'src/lib/utils';

// API Hooks
import useRebateAgreement from 'src/api/rebates/useRebateAgreement';
import { useListVersions } from 'src/api/rebates/useRebateVersions';
import useDivisions from 'src/api/organization/useDivisions';
import { useRebateInvoicesByAgreement } from 'src/api/rebates/useRebateInvoices';
import { useSupplierPaymentsByAgreement } from 'src/api/rebates/useSupplierPayments';
import { useManualAmountsByAgreement } from 'src/api/rebates/useManualAmounts';
import {
  useGetDocument,
  GetDocumentResponse,
} from 'src/api/core/documents/useGetDocument';
import {
  useExtractRulesOnly,
  ExtractRulesOnlyResponse,
  useCreateVersionDraft,
} from 'src/api/rebates/useCreateVersionWithExtraction';

// Step Components (to be implemented)
import Step1UploadPDFs from './create-version/Step1UploadPDFs';
import Step2ReviewRules from './create-version/Step2ReviewRules';
import Step3PreviewImpact from './create-version/Step3PreviewImpact';

// Types
import type { SupplierPayment, PaymentAllocation } from 'src/api/rebates/types';
import type { ManualAmount } from 'src/api/rebates/useManualAmounts';
import { DeltaAnalysisResult } from '@erp/shared-types';
import { RuleWithVersionState } from './create-version/types';
import RawFilePreviewPopout from '@/components/common/RawFilePreviewPopout';

interface ExistingData {
  supplierPayments: SupplierPayment[];
  allocations: PaymentAllocation[];
  manualAmounts: ManualAmount[];
}

interface AllocationImpact {
  preserved: PaymentAllocation[];
  removed: PaymentAllocation[];
  preservedAmount: number;
  removedAmount: number;
}

interface ManualAmountImpact {
  preserved: ManualAmount[];
  removed: ManualAmount[];
  preservedAmount: number;
  removedAmount: number;
}

interface CreateVersionState {
  currentStep: number;
  uploadedFiles: File[];
  allRules: RuleWithVersionState[]; // Combined old + new rules with version state
  productGroups: ExtractRulesOnlyResponse['productGroups'];
  extractionMetadata?: ExtractRulesOnlyResponse['extractionMetadata'];
  deltaAnalysis?: DeltaAnalysisResult;
  newVersionId?: string;
  isLoading: boolean;
  error?: string;
  completedSteps: Set<number>;
}

const STEPS = [
  {
    id: 1,
    title: 'Upload PDFs',
    description: 'Upload agreement documents for AI extraction',
    icon: UploadIcon,
  },
  {
    id: 2,
    title: 'Review Rules',
    description: 'Compare extracted rules with previous version',
    icon: FileTextIcon,
  },
  {
    id: 3,
    title: 'Confirm & Create',
    description: 'Review impact and create new version',
    icon: CheckCircleIcon,
  },
];

const RebateAgreementCreateVersionPage: React.FC = () => {
  const { id } = useParams({
    from: '/authenticated/rebates/agreements/$id/create-version',
  });
  const navigate = useNavigate();

  // State management
  const [state, setState] = useState<CreateVersionState>({
    currentStep: 1,
    uploadedFiles: [], // Single source of truth for uploaded files
    allRules: [], // Unified list of wrapped rules
    productGroups: {
      matched: [],
      new: [],
      all: [],
    },
    extractionMetadata: undefined,
    deltaAnalysis: undefined,
    isLoading: false,
    completedSteps: new Set(),
  });

  // Data fetching
  const {
    data: agreement,
    isLoading: agreementLoading,
    error: agreementError,
  } = useRebateAgreement(id || '', !!id);

  const { data: versions, isLoading: versionsLoading } = useListVersions(
    agreement?.original_agreement_id || '',
    !!agreement?.original_agreement_id,
  );

  const { data: divisionsData, isLoading: divisionsLoading } = useDivisions(
    !!agreement?.original_agreement_id,
  );

  // Fetch invoices for download functionality
  const { data: invoicesData } = useRebateInvoicesByAgreement(
    agreement?.original_agreement_id || '',
    undefined, // no filters
    !!agreement?.original_agreement_id,
  );

  // Fetch existing supplier payments and allocations
  const { data: supplierPaymentsData } = useSupplierPaymentsByAgreement(
    agreement?.original_agreement_id || '',
    !!agreement?.original_agreement_id,
  );

  // Fetch existing manual amounts
  const { data: manualAmountsData } = useManualAmountsByAgreement(
    agreement?.original_agreement_id || '',
    undefined, // no date filters
    undefined,
    !!agreement?.original_agreement_id,
  );

  // Document download functionality
  const { mutate: fetchDocument } = useGetDocument();

  const handleDownloadDocument = (documentId: string, fileName?: string) => {
    if (!documentId) {
      console.error('No document ID provided');
      return;
    }

    fetchDocument(
      { documentId: documentId },
      {
        onSuccess: (response: { data: GetDocumentResponse }) => {
          if (response.data.signedUrl) {
            window.open(response.data.signedUrl, '_blank');
          }
        },
        onError: (error: Error) => {
          console.error('Failed to download document:', error);
        },
      },
    );
  };

  // Get the latest finalized version (source for comparison)
  const latestFinalizedVersion = versions?.find(
    (v) =>
      v.status === 'finalized' && v.versionNumber === agreement?.version_number,
  );

  // Version creation mutation (after latestFinalizedVersion is computed)
  const createDraftMutation = useCreateVersionDraft(
    latestFinalizedVersion?.id || '',
  );

  // Helper functions to calculate impacts
  const calculateAllocationImpact = (
    allocations: PaymentAllocation[],
    restoredRuleIds: Set<string>,
  ): AllocationImpact => {
    const preserved = allocations.filter((allocation) =>
      restoredRuleIds.has(allocation.rebateRuleId),
    );
    const removed = allocations.filter(
      (allocation) => !restoredRuleIds.has(allocation.rebateRuleId),
    );

    return {
      preserved,
      removed,
      preservedAmount: preserved.reduce((sum, a) => sum + a.allocatedAmount, 0),
      removedAmount: removed.reduce((sum, a) => sum + a.allocatedAmount, 0),
    };
  };

  const calculateManualAmountImpact = (
    manualAmounts: ManualAmount[],
    restoredRuleIds: Set<string>,
  ): ManualAmountImpact => {
    const preserved = manualAmounts.filter((amount) =>
      restoredRuleIds.has(amount.ruleId),
    );
    const removed = manualAmounts.filter(
      (amount) => !restoredRuleIds.has(amount.ruleId),
    );

    return {
      preserved,
      removed,
      preservedAmount: preserved.reduce(
        (sum, a) => sum + parseFloat(a.amount),
        0,
      ),
      removedAmount: removed.reduce((sum, a) => sum + parseFloat(a.amount), 0),
    };
  };

  // Process existing data and calculate impacts when data loads or included rules change
  useEffect(() => {
    if (supplierPaymentsData && manualAmountsData) {
      // Extract all allocations from supplier payments
      const allAllocations: PaymentAllocation[] = [];
      supplierPaymentsData.forEach((payment) => {
        if (payment.allocations) {
          allAllocations.push(...payment.allocations);
        }
      });

      const existingData: ExistingData = {
        supplierPayments: supplierPaymentsData,
        allocations: allAllocations,
        manualAmounts: manualAmountsData,
      };

      // Get rule IDs that will be included (previous rules that are toggled on)
      const includedPreviousRuleIds = new Set(
        state.allRules
          .filter((r) => r.source === 'previous' && r.includeInNewVersion)
          .map((r) => r.rule.id),
      );

      // Calculate impacts based on included previous rules
      const allocationImpact = calculateAllocationImpact(
        allAllocations,
        includedPreviousRuleIds,
      );
      const manualAmountImpact = calculateManualAmountImpact(
        manualAmountsData,
        includedPreviousRuleIds,
      );

      setState((prev) => ({
        ...prev,
        existingData,
        allocationImpact,
        manualAmountImpact,
      }));
    }
  }, [supplierPaymentsData, manualAmountsData, state.allRules]);

  // Navigation handlers
  const handleBack = () => {
    if (state.currentStep > 1) {
      setState((prev) => ({ ...prev, currentStep: prev.currentStep - 1 }));
    }
  };

  const handleCancel = () => {
    navigate({
      to: '/rebates/agreements/$id',
      params: { id: id || '' },
    });
  };

  // Step completion handlers with error handling
  const handleStep1Complete = (
    files: File[],
    wrappedRules: RuleWithVersionState[],
    productGroups: ExtractRulesOnlyResponse['productGroups'],
    extractionMetadata: ExtractRulesOnlyResponse['extractionMetadata'],
  ) => {
    setState((prev) => ({
      ...prev,
      uploadedFiles: files,
      allRules: wrappedRules, // Store wrapped extracted rules
      productGroups,
      extractionMetadata,
      isLoading: false,
      error: undefined,
      currentStep: 2,
      completedSteps: new Set([...prev.completedSteps, 1]),
    }));
  };

  const handleStep1Error = (error: string) => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
      error,
    }));
  };

  const handleStep2Complete = (
    updatedRules: RuleWithVersionState[],
    updatedProductGroups: ExtractRulesOnlyResponse['productGroups'],
  ) => {
    setState((prev) => ({
      ...prev,
      allRules: updatedRules,
      productGroups: updatedProductGroups, // ✅ persist changes
      error: undefined,
      currentStep: 3,
      completedSteps: new Set([...prev.completedSteps, 2]),
    }));
  };

  const handleStep2Error = (error: string) => {
    setState((prev) => ({
      ...prev,
      error,
    }));
  };

  const handleCreateVersion = async (deltaAnalysis: DeltaAnalysisResult) => {
    if (!latestFinalizedVersion?.id) {
      setState((prev) => ({
        ...prev,
        error: 'Previous version not found',
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      deltaAnalysis,
      error: undefined,
    }));

    try {
      // Filter rules to include in new version and separate by source
      const includedRules = state.allRules.filter((r) => r.includeInNewVersion);

      const extractedRules = includedRules
        .filter((r) => r.source === 'extracted' || r.source === 'manual')
        .map((r) => r.rule);

      const restoredRuleIds = includedRules
        .filter((r) => r.source === 'previous')
        .map((r) => r.rule.id);

      const result = await createDraftMutation.mutateAsync({
        extractedRules,
        restoredRuleIds,
        files: state.uploadedFiles,
      });

      setState((prev) => ({
        ...prev,
        completedSteps: new Set([...prev.completedSteps, 3]),
      }));

      // Navigate to the new version
      navigate({
        to: '/rebates/agreements/$id',
        params: { id: result.data.newVersionId },
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create version draft';
      console.error('Failed to create version draft:', err);
      setState((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    }
  };

  const handleStep3Complete = (deltaAnalysis: DeltaAnalysisResult) => {
    setState((prev) => ({
      ...prev,
      deltaAnalysis,
      error: undefined,
      completedSteps: new Set([...prev.completedSteps, 3]),
    }));
  };

  const handleStep3Error = (error: string) => {
    setState((prev) => ({
      ...prev,
      error,
    }));
  };

  // Loading and error states
  if (agreementLoading || versionsLoading || divisionsLoading) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">
                    Loading agreement details...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (agreementError || !agreement || !latestFinalizedVersion) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <WarningIcon className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">
                  Error Loading Agreement
                </h2>
                <p className="text-muted-foreground mb-4">
                  {agreementError?.message ||
                    'Agreement not found or no finalized version available.'}
                </p>
                <Button onClick={handleCancel} variant="outline">
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back to Agreement
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const progress = (state.currentStep / STEPS.length) * 100;

  const simpleDivisions = divisionsData?.divisions.map((d) => ({
    id: d.id,
    name: d.name,
    tags: d.tags?.map((t) => t.id) || [],
  }));

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Create New Version</h1>
              <p className="text-muted-foreground">
                Creating version {latestFinalizedVersion.versionNumber + 1} from{' '}
                <span className="font-medium">{agreement.name}</span>
              </p>
            </div>
            {/* <Button onClick={handleCancel} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Cancel
            </Button> */}
          </div>

          {/* Progress Bar */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Progress</span>
                  <span className="text-sm text-muted-foreground">
                    Step {state.currentStep} of {STEPS.length}
                  </span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          {/* Stepper */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                {STEPS.map((step, index) => {
                  const isActive = state.currentStep === step.id;
                  const isCompleted = state.currentStep > step.id;
                  const Icon = step.icon;

                  return (
                    <div key={step.id} className="flex items-center">
                      <div className="flex flex-col items-center">
                        <div
                          className={cn(
                            'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                            isCompleted
                              ? 'bg-primary border-primary text-primary-foreground'
                              : isActive
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'bg-background border-muted-foreground text-muted-foreground',
                          )}
                        >
                          {isCompleted ? (
                            <CheckIcon className="h-5 w-5" />
                          ) : (
                            <Icon className="h-5 w-5" />
                          )}
                        </div>
                        <div className="mt-2 text-center">
                          <p
                            className={cn(
                              'text-sm font-medium',
                              isActive
                                ? 'text-primary'
                                : 'text-muted-foreground',
                            )}
                          >
                            {step.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {step.description}
                          </p>
                        </div>
                      </div>
                      {index < STEPS.length - 1 && (
                        <div className="flex-1 h-px bg-border mx-4" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Step Content */}
          <Card>
            <CardContent className="p-6">
              {state.currentStep === 1 && (
                <Step1UploadPDFs
                  agreement={agreement}
                  latestVersion={latestFinalizedVersion}
                  onComplete={handleStep1Complete}
                  onError={handleStep1Error}
                  isLoading={state.isLoading}
                  error={state.error}
                  selectedFiles={state.uploadedFiles}
                  setSelectedFiles={(files) =>
                    setState((prev) => ({ ...prev, uploadedFiles: files }))
                  }
                />
              )}
              {state.currentStep === 2 && (
                <Step2ReviewRules
                  agreement={agreement}
                  latestVersion={latestFinalizedVersion}
                  wrappedRules={state.allRules}
                  productGroups={state.productGroups}
                  divisions={simpleDivisions || []}
                  onComplete={handleStep2Complete}
                  onError={handleStep2Error}
                  onBack={handleBack}
                  isLoading={state.isLoading}
                  error={state.error}
                />
              )}
              {state.currentStep === 3 && (
                <Step3PreviewImpact
                  agreement={agreement}
                  latestVersion={latestFinalizedVersion}
                  allRules={state.allRules}
                  productGroups={state.productGroups}
                  onComplete={handleStep3Complete}
                  onError={handleStep3Error}
                  onBack={handleBack}
                  isLoading={state.isLoading}
                  error={state.error}
                  invoices={invoicesData?.invoices}
                  onDownloadDocument={handleDownloadDocument}
                  onCreateVersion={handleCreateVersion}
                  isCreating={createDraftMutation.isPending}
                />
              )}
            </CardContent>
          </Card>
          {state.uploadedFiles.length > 0 && (
            <Card>
              <CardContent>
                <RawFilePreviewPopout
                  selectedFiles={state.uploadedFiles}
                  className="mt-0"
                  showCard={false}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RebateAgreementCreateVersionPage;
